import { useEffect, useLayoutEffect, useState, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import { AxiosInstance } from 'axios';

import Axios from '../../../../helpers/http';
import toQueryString from '../../helpers/querystring/toQueryString';
import getQueryOptions from '../../helpers/querystring/getQueryOptions';
import updateHistory from '../../helpers/history/updateHistory';
import getFilterGroups from '../../helpers/filters/getFilterGroups';
import { QueryOptions } from '../../interfaces';

const defaultQueryOptions = { page: '1', page_size: '50', search: '', ordering: '' };

interface Shimmer {
  loading: boolean;
  quantity: number;
}

export interface RestLedgerProps {
  url: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  render: Function;
  queryOptions?: QueryOptions;
  externals?: Record<string, unknown>;
  axios?: AxiosInstance;
}

// @ts-ignore
export default function RestLedger({ render, url, queryOptions = {}, externals = {}, axios = Axios }: RestLedgerProps) {
  const initialQueryOptions = { ...defaultQueryOptions, ...queryOptions };
  const [currentQueryOptions, setQueryOptions] = useState<QueryOptions>(getQueryOptions(initialQueryOptions));
  const [shimmer, setShimmer] = useState<Shimmer>({
    loading: true,
    quantity: Number(getQueryOptions(currentQueryOptions).page_size),
  });
  const [data, setData] = useState<any[] | null>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [apiResponse, setApiResponse] = useState<Record<string, unknown>>({});

  const fetchData = token => {
    setShimmer({ ...shimmer, loading: true });
    const queryString = toQueryString(currentQueryOptions);
    updateHistory(queryString);
    const externalsQueryString = toQueryString(externals);

    axios
      .get(`${url}?${queryString}&${externalsQueryString}`, { cancelToken: token })
      .then(response => {
        const { data } = response;
        const { results, ...rest } = data;

        const sameQueryOptions = isEqual(initialQueryOptions, currentQueryOptions);
        const items = sameQueryOptions && results && results.length === 0 ? null : results;

        setData(items);
        setApiResponse({ ...response, ...rest });
        setTotalPages(data.num_pages);
        setShimmer({ ...shimmer, loading: false });
      })
      .catch(error => {
        const proto = Object.getPrototypeOf(error);
        // eslint-disable-next-line no-underscore-dangle
        const isCancel = proto.__CANCEL__ === true;
        if (isCancel) {
          /*
                If the request has been cancelled, it's expected and shouldn't cause the
                ledger to go into an error state (state.error = true) or change the
                loading state (state.loading = false). So we don't do anything.
              */
          return;
        }

        setData(null);
        setApiResponse(error.response);
        setTotalPages(1);
        setShimmer({ ...shimmer, loading: false });
      });
  };

  useLayoutEffect(() => {
    const CancelToken = Axios.CancelToken;
    const source = CancelToken.source();

    if (url) {
      fetchData(source.token);
    }

    return () => {
      source.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, toQueryString(currentQueryOptions), toQueryString(externals)]);

  useEffect(() => {
    const CancelToken = Axios.CancelToken;
    const source = CancelToken.source();
    window.onpopstate = () => {
      setQueryOptions(prevQueryOptions => getQueryOptions(prevQueryOptions));
      fetchData(source.token);
    };
    return () => {
      source.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const state = useMemo(() => {
    const onChangePage = (page: number): void => {
      setQueryOptions(prevQueryOptions => ({ ...prevQueryOptions, page: page.toString() }));
    };

    const onChangeSearch = e => {
      setQueryOptions(prevQueryOptions => ({
        ...prevQueryOptions,
        page: '1',
        search: e.target.value.trim(),
      }));
    };

    const onChangeFilter = filter => {
      const filterQuery = filter.reduce((query, { key, items }) => {
        const selectedItem = items.find(i => i.checked);
        if (selectedItem) {
          return { ...query, [key]: selectedItem.value };
        }
        return query;
      }, {});

      setQueryOptions(prevQueryOptions => ({
        page: 1,
        page_size: prevQueryOptions.page_size,
        search: prevQueryOptions.search,
        ordering: prevQueryOptions.ordering,
        ...filterQuery,
        ...queryOptions,
      }));
    };

    const onSort = ordering => {
      setQueryOptions(prevQueryOptions => ({ ...prevQueryOptions, page: '1', ordering }));
    };

    const search = {
      onChange: onChangeSearch,
      value: currentQueryOptions.search,
    };

    const pagination = {
      onChange: onChangePage,
      totalPages,
      currentPage: Number(currentQueryOptions.page),
    };

    const filter = {
      getFilterGroups: getFilterGroups(currentQueryOptions),
      onChange: onChangeFilter,
    };

    return {
      data,
      sort: onSort,
      fetchData,
      shimmer,
      search,
      pagination,
      filter,
      ordering: currentQueryOptions.ordering,
      apiResponse,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, toQueryString(currentQueryOptions), shimmer.loading, data]);

  return render(state);
}

RestLedger.displayName = 'RestLedger';
